import { useEffect, useState } from "react";

// A shared registry of "exclusive" blocking modals — imperative antd
// Modal.confirm dialogs that demand a data-safety decision (e.g. the stale-write
// "This record changed" conflict dialog in Resources.saga). While at least one
// such modal is open, the unsaved-changes navigation guard
// (BlockingNavigationModal) suppresses its own popover, so only ONE warning
// dialog is ever presented at a time.
//
// The two systems are otherwise uncoordinated and used to stack: the leave-guard
// is a MUI Popover forced to zIndex 20000 and coordinated only among leave-guards
// via NavigationPromptContext, while the conflict dialog is an antd Modal.confirm
// at the default zIndex 1000. When both opened, the leave-guard sat on top and
// interactive with the conflict dialog dead behind it — see
// operator-task-fe-overlapping-warning-popups-and-record-changed-dialog-styling.

let openCount = 0;
const listeners = new Set<() => void>();

const emit = (): void => listeners.forEach((listener) => listener());

/**
 * Register an exclusive blocking modal as open. Returns a release() to call when
 * it closes. release() is idempotent and never drives the count below zero, so a
 * double-release (e.g. both an explicit call and an afterClose backstop) is safe.
 */
export const registerExclusiveBlockingModal = (): (() => void) => {
    openCount += 1;
    emit();
    let released = false;
    return () => {
        if (released) {
            return;
        }
        released = true;
        openCount = Math.max(0, openCount - 1);
        emit();
    };
};

export const isExclusiveBlockingModalOpen = (): boolean => openCount > 0;

// React 17-compatible external-store subscription (no useSyncExternalStore),
// mirroring useStructuredDataDisplayMode. Every subscriber re-renders when an
// exclusive blocking modal opens or closes.
export const useExclusiveBlockingModalOpen = (): boolean => {
    const [open, setOpen] = useState<boolean>(openCount > 0);
    useEffect(() => {
        const listener = () => setOpen(openCount > 0);
        listeners.add(listener);
        // Re-sync in case the count changed between render and effect commit.
        listener();
        return () => {
            listeners.delete(listener);
        };
    }, []);
    return open;
};
